export interface DateRangeValidationConstraints {
    min?: Date;
    max?: Date;
    maxDaySpan?: number;
    includeEndDate?: boolean;
}

export type DateRangeValidationReason =
    | 'missing'
    | 'startAfterEnd'
    | 'outOfRange'
    | 'maxDaySpan';

export interface DateRangeValidationResult {
    isValid: boolean;
    reason: DateRangeValidationReason | null;
    daySpan: number | null;
}

export type DateRangeValidationMessages = Partial<
    Record<DateRangeValidationReason, string>
>;

export const getDateRangeDaySpan = (
    start: Date,
    end: Date,
    includeEndDate = false
): number => {
    const daySpan = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    return includeEndDate ? daySpan + 1 : daySpan;
};

export const validateDateRange = (
    start: Date | null,
    end: Date | null,
    constraints: DateRangeValidationConstraints = {}
): DateRangeValidationResult => {
    if (!start || !end) {
        return { isValid: false, reason: 'missing', daySpan: null };
    }

    if (start >= end) {
        return { isValid: false, reason: 'startAfterEnd', daySpan: null };
    }

    if ((constraints.min && start < constraints.min) || (constraints.max && end > constraints.max)) {
        return { isValid: false, reason: 'outOfRange', daySpan: null };
    }

    const daySpan = getDateRangeDaySpan(start, end, constraints.includeEndDate);

    if (constraints.maxDaySpan && daySpan > constraints.maxDaySpan) {
        return { isValid: false, reason: 'maxDaySpan', daySpan };
    }

    return { isValid: true, reason: null, daySpan };
};

export const getDateRangeValidationMessage = (
    result: DateRangeValidationResult,
    messages: DateRangeValidationMessages
): string | null => {
    if (!result.reason || result.reason === 'missing') {
        return null;
    }

    return messages[result.reason] ?? null;
};